/**
 * Loan state engine.
 *
 * Computes the current loop-loan balance from the user's purchases (with
 * `loanFundedUsd` set) plus standalone LoanEvents (borrow / repayment /
 * balance-override).
 *
 * --- How the balance is computed ---
 *
 * 1. Build a unified, time-ordered list of events:
 *      - synthetic 'borrow' for each purchase with loanFundedUsd > 0
 *      - explicit LoanEvents from the loanEvents store
 *
 * 2. Walk the timeline. Between event N and event N+1, the balance accrues
 *    interest daily at the APR active at event N (i.e., the APR that was
 *    snapshotted when the most recent borrow / override happened). On event N+1,
 *    apply the event's effect:
 *        borrow:           balance += amount;  active APR = event.aprSnapshot
 *        repayment:        balance -= amount   (floored at 0)
 *        balance-override: balance := amount;  if event.aprSnapshot present, also update active APR
 *
 * 3. Continue accruing from the last event up to "now" (or whatever asOf is passed).
 *
 * Currency precision: all balance math uses Decimal. Daily compounding is
 *    next = prev * (1 + apr/365). For 365 days at 5%, this gives ~5.13%
 *    effective annual, which is the standard convention for variable-rate
 *    consumer-grade reporting and matches what Aave shows in its UI.
 *
 * Edge cases:
 *  - No events at all → returns zero balance.
 *  - Borrow with no aprSnapshot → uses fallback APR (passed to function), or 0
 *    if no fallback. We don't infer from app state to keep this engine pure.
 *  - All events in the future relative to asOf → returns zero balance and 0
 *    accrued interest (the user hasn't borrowed yet).
 */

import { Decimal, ZERO, ONE } from './money.js';
import type { Usd } from './money.js';
import type { LoanEvent, PurchaseTransaction } from './types.js';

const DAYS_PER_YEAR = new Decimal(365);

export interface LoanStateInputs {
  purchases: PurchaseTransaction[];
  loanEvents: LoanEvent[];
  /** "Now"  -  defaults to current Date when computing. */
  asOf: Date;
  /** Fallback APR if a borrow event has no aprSnapshot. Decimal (0.0573 = 5.73%). */
  fallbackApr: Decimal;
}

export interface LoanStateRow {
  date: Date;
  /** What kind of effect this row had. 'accrue' is a synthetic snapshot, no event. */
  kind: 'borrow' | 'repayment' | 'balance-override' | 'accrue';
  /** Balance after this row's effect was applied. */
  balanceAfter: Usd;
  /** USD amount associated with this row (positive for all kinds; effect is by kind). */
  amountUsd?: Usd;
  /** Active APR after this row (used for next-step accrual). */
  activeAprAfter: Decimal;
  /** Days since the previous row (for interest accrual). */
  daysSincePrev: number;
  /** Interest accrued from previous row to this one (USD). */
  interestAccrued: Usd;
  /** Source event id, if any. */
  eventId?: string;
  note?: string;
}

export interface LoanState {
  /** Balance now (asOf), after accrual from the last event to asOf. */
  currentBalance: Usd;
  /** Active APR (used to project further accrual). */
  currentApr: Decimal;
  /** Sum of all borrows ever (no interest, no repayments). */
  totalBorrowed: Usd;
  /** Sum of all repayments ever. */
  totalRepaid: Usd;
  /** Sum of all interest ever accrued (currentBalance + totalRepaid - totalBorrowed - all override deltas). */
  totalInterestAccrued: Usd;
  /** Detailed timeline including a final "accrue-to-now" row. */
  timeline: LoanStateRow[];
  /** Date of the most recent loan event (borrow / repayment / override), or null if none. */
  lastEventDate: Date | null;
}

/** Days between two dates (decimal). Calendar days, not 24h ticks. */
function daysBetween(a: Date, b: Date): Decimal {
  const ms = b.getTime() - a.getTime();
  return new Decimal(ms).div(86_400_000);
}

/** Apply daily compounding for `days` at `apr` (decimal). new = prev * (1 + apr/365)^days. */
function compound(balance: Decimal, apr: Decimal, days: Decimal): Decimal {
  if (balance.isZero() || apr.isZero() || days.lessThanOrEqualTo(0)) return balance;
  const dailyRate = apr.div(DAYS_PER_YEAR);
  // Decimal.js exponent only handles integer powers natively; use ln/exp for fractional days.
  const factor = ONE.plus(dailyRate).ln().times(days).exp();
  return balance.times(factor);
}

interface NormalizedEvent {
  date: Date;
  kind: 'borrow' | 'repayment' | 'balance-override';
  amountUsd: Usd;
  aprSnapshot?: Decimal;
  id: string;
  note?: string;
}

/** Convert purchases-with-loanFundedUsd into synthetic borrow events. */
function purchasesToSyntheticBorrows(purchases: PurchaseTransaction[]): NormalizedEvent[] {
  const out: NormalizedEvent[] = [];
  for (const p of purchases) {
    if (p.loanFundedUsd && p.loanFundedUsd.greaterThan(0)) {
      const ev: NormalizedEvent = {
        date: p.date,
        kind: 'borrow',
        amountUsd: p.loanFundedUsd,
        id: `purchase:${p.id}`,
      };
      if (p.loanAprAtTx !== undefined) ev.aprSnapshot = p.loanAprAtTx;
      if (p.note !== undefined) ev.note = p.note;
      out.push(ev);
    }
  }
  return out;
}

/** Convert explicit LoanEvents to the normalized shape. */
function explicitToNormalized(events: LoanEvent[]): NormalizedEvent[] {
  return events.map(e => {
    const out: NormalizedEvent = {
      date: e.date,
      kind: e.kind,
      amountUsd: e.amountUsd,
      id: e.id,
    };
    if (e.aprSnapshot !== undefined) out.aprSnapshot = e.aprSnapshot;
    if (e.note !== undefined) out.note = e.note;
    return out;
  });
}

/**
 * Walk the merged event list and produce a full timeline + summary.
 * The timeline includes a final 'accrue' row that represents accrual from the
 * last event up to asOf.
 */
export function computeLoanState(inputs: LoanStateInputs): LoanState {
  const merged: NormalizedEvent[] = [
    ...purchasesToSyntheticBorrows(inputs.purchases),
    ...explicitToNormalized(inputs.loanEvents),
  ].sort((a, b) => {
    const t = a.date.getTime() - b.date.getTime();
    if (t !== 0) return t;
    // Stable secondary sort for same-day events: borrows before overrides before repayments.
    // (Matters when the same day has both  -  the borrow happens, then the override pins the
    // balance, then the repayment reduces from the pinned balance.)
    const order = { borrow: 0, 'balance-override': 1, repayment: 2 } as const;
    return order[a.kind] - order[b.kind];
  });

  const timeline: LoanStateRow[] = [];
  let balance = ZERO as Usd;
  let activeApr = inputs.fallbackApr;
  let totalBorrowed = ZERO as Usd;
  let totalRepaid = ZERO as Usd;
  let totalInterest = ZERO as Usd;
  let cursor: Date | null = null;
  let lastEventDate: Date | null = null;

  for (const ev of merged) {
    // Skip events in the future relative to asOf  -  they haven't happened yet.
    if (ev.date.getTime() > inputs.asOf.getTime()) continue;

    // Accrue interest from cursor to event date.
    let interestThis = ZERO as Usd;
    let days = new Decimal(0);
    if (cursor !== null && balance.greaterThan(0)) {
      days = daysBetween(cursor, ev.date);
      const newBalance = compound(balance, activeApr, days);
      interestThis = newBalance.minus(balance) as Usd;
      totalInterest = totalInterest.plus(interestThis) as Usd;
      balance = newBalance as Usd;
    }

    // Apply event effect.
    if (ev.kind === 'borrow') {
      balance = balance.plus(ev.amountUsd) as Usd;
      totalBorrowed = totalBorrowed.plus(ev.amountUsd) as Usd;
      if (ev.aprSnapshot !== undefined) activeApr = ev.aprSnapshot;
    } else if (ev.kind === 'repayment') {
      balance = Decimal.max(0, balance.minus(ev.amountUsd)) as Usd;
      totalRepaid = totalRepaid.plus(ev.amountUsd) as Usd;
    } else {
      // balance-override
      balance = ev.amountUsd;
      if (ev.aprSnapshot !== undefined) activeApr = ev.aprSnapshot;
    }

    timeline.push({
      date: ev.date,
      kind: ev.kind,
      balanceAfter: balance,
      amountUsd: ev.amountUsd,
      activeAprAfter: activeApr,
      daysSincePrev: days.toNumber(),
      interestAccrued: interestThis,
      eventId: ev.id,
      ...(ev.note !== undefined && { note: ev.note }),
    });

    cursor = ev.date;
    lastEventDate = ev.date;
  }

  // Final accrual row from the last event (or no events) up to asOf.
  if (cursor !== null && balance.greaterThan(0)) {
    const days = daysBetween(cursor, inputs.asOf);
    if (days.greaterThan(0)) {
      const newBalance = compound(balance, activeApr, days);
      const interestThis = newBalance.minus(balance) as Usd;
      totalInterest = totalInterest.plus(interestThis) as Usd;
      balance = newBalance as Usd;
      timeline.push({
        date: inputs.asOf,
        kind: 'accrue',
        balanceAfter: balance,
        activeAprAfter: activeApr,
        daysSincePrev: days.toNumber(),
        interestAccrued: interestThis,
      });
    }
  }

  return {
    currentBalance: balance,
    currentApr: activeApr,
    totalBorrowed,
    totalRepaid,
    totalInterestAccrued: totalInterest,
    timeline,
    lastEventDate,
  };
}
